(function () {
  'use strict';

  const ROOT_ID         = 'cart-upsell';
  const TRACK_ID        = 'cart-upsell-track';
  const TEMPLATE_ID     = 'cart-upsell-card-template';
  const RECOMMEND_LIMIT = 6;
  const STORAGE_KEY     = 'cartUpsell:lastProductId';

  const root         = () => document.getElementById(ROOT_ID);
  const track        = () => document.getElementById(TRACK_ID);
  const cardTemplate = () => document.getElementById(TEMPLATE_ID);

  // ── Money formatting ───────────────────────────────────────

  function moneyFormat(cents) {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency, maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  // ── Cart API ───────────────────────────────────────────────

  async function getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Failed to load cart');
    return res.json();
  }

  // ── Upsell recommendation fetch ────────────────────────────

  async function fetchRecommendations(productId) {
    const url = `/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${RECOMMEND_LIMIT}&intent=related`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Failed to load recommendations');
    const data = await res.json();
    return data.products || [];
  }

  // ── Session storage helpers ────────────────────────────────

  function setLastProductId(id) {
    if (!id) return;
    try { sessionStorage.setItem(STORAGE_KEY, String(id)); } catch (e) {}
  }
  function getLastProductId() {
    try { return sessionStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
  }

  // ── SVG icons (same ones the theme uses) ──────────────────

  const SVG_TRASH = `<svg class="icon" aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 3.98665C11.78 3.76665 9.54667 3.65332 7.32 3.65332C6 3.65332 4.68 3.71999 3.36 3.85332L2 3.98665" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M5.66602 3.31301L5.81268 2.43967C5.91935 1.80634 5.99935 1.33301 7.12602 1.33301H8.87268C9.99935 1.33301 10.086 1.83301 10.186 2.44634L10.3327 3.31301" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M12.5669 6.09375L12.1336 12.8071C12.0603 13.8537 12.0003 14.6671 10.1403 14.6671H5.86026C4.00026 14.6671 3.94026 13.8537 3.86693 12.8071L3.43359 6.09375" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M6.88672 11H9.10672" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M6.33398 8.33301H9.66732" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;

  const SVG_MINUS = `<svg class="icon" aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 12H18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;

  const SVG_PLUS = `<svg class="icon" aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 12H18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M12 18V6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;

  // ── Build a cart item row from a cart.js item object ───────
  //
  // Uses the same classes/data-attributes as the Liquid template
  // so the theme's delegated event handlers (quantity, remove)
  // pick it up automatically — no extra JS wiring needed.

  function buildCartItemHTML(item, index) {
    const isMinQty      = item.quantity <= 1;
    const variantLabel  = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="cart-item__details-text">${item.variant_title}</p>` : '';
    const imageUrl      = item.featured_image && item.featured_image.url
      ? item.featured_image.url
      : (item.image || '');
    const image2x       = imageUrl
      ? imageUrl.replace(/(\.(jpg|jpeg|png|webp))(\?|$)/i, '_200x200$1$3')
      : '';
    const image1x       = imageUrl
      ? imageUrl.replace(/(\.(jpg|jpeg|png|webp))(\?|$)/i, '_100x100$1$3')
      : '';

    return `
      <div class="cart-item" id="CartItem-${index}" data-index="${index}" data-cart-item="${item.key}">
        <div class="cart-item__media">
          <a href="${item.url}" class="cart-item__image-container focus-visible-outline" tabindex="0">
            <picture>
              <img class="cart-item__image"
                src="${image1x}"
                srcset="${image1x} 1x, ${image2x} 2x"
                width="100" height="100"
                alt="${item.title.replace(/"/g, '&quot;')}"
                loading="lazy">
            </picture>
          </a>
        </div>
        <div class="cart-item__content">
          <div class="cart-item__row">
            <div class="cart-item__details">
              <div class="cart-item__heading">
                <a href="${item.url}" class="focus-visible-outline cart-item__details-title" tabindex="0">${item.title}</a>
                <a href="/cart/change?id=${item.key}&quantity=0"
                  class="focus-visible-outline cart-item__cta js-cart-item-remove"
                  id="Remove-${index}"
                  aria-label="Remove"
                  data-index="${index}"
                  data-item-key="${item.key}"
                  tabindex="0">
                  ${SVG_TRASH}
                </a>
              </div>
              ${variantLabel}
            </div>
            <div class="cart-item__actions">
              <div class="cart-item__quantity">
                <div class="cart-item__quantity-wrapper">
                  <label class="visually-hidden" for="Quantity-${index}">Quantity</label>
                  <div class="quantity js-quantity">
                    <button class="focus-visible-outline quantity__button${isMinQty ? ' is-disabled' : ''}"
                      name="minus" type="button" data-quantity-decrease=""
                      aria-label="Decrease quantity for ${item.title.replace(/"/g, '&quot;')}"
                      ${isMinQty ? 'disabled=""' : ''} tabindex="0">
                      ${SVG_MINUS}
                    </button>
                    <input class="focus-visible-outline quantity__input"
                      id="Quantity-${index}"
                      type="number" name="updates[]" pattern="[0-9]*"
                      value="${item.quantity}" min="1" step="1"
                      data-index="${index}"
                      data-item-key="${item.key}"
                      data-quantity-input=""
                      aria-label="Quantity for ${item.title.replace(/"/g, '&quot;')}"
                      tabindex="0">
                    <button class="focus-visible-outline quantity__button"
                      name="plus" type="button" data-quantity-increase=""
                      aria-label="Increase quantity for ${item.title.replace(/"/g, '&quot;')}"
                      tabindex="0">
                      ${SVG_PLUS}
                    </button>
                  </div>
                </div>
              </div>
              <div class="cart-item__prices">
                <div class="cart-item__price">${moneyFormat(item.final_line_price)}</div>
              </div>
            </div>
          </div>
          <div class="cart-item__error js-cart-item-error" id="LineItemError-${index}" role="alert">
            <small class="cart-item__error-text js-cart-item-error-message"></small>
          </div>
        </div>
      </div>`.trim();
  }

  // ── Qty button handler for injected rows ─────────────────
  //
  // The theme attaches qty handlers on page load. Rows we inject
  // later are invisible to that init pass, so their +/- buttons
  // are dead. This function wires our own handlers immediately
  // after insertion. We call /cart/change.js directly; the global
  // fetch patch then fires syncCartDOM() + render() automatically.

  function attachQtyHandlers(row, itemKey) {
    const input    = row.querySelector('.quantity__input');
    const minusBtn = row.querySelector('[data-quantity-decrease]');
    const plusBtn  = row.querySelector('[data-quantity-increase]');
    if (!input || !minusBtn || !plusBtn) return;

    async function changeQty(delta) {
      const current = parseInt(input.value || '1', 10);
      const newQty  = Math.max(1, current + delta);

      // Optimistic UI update so the button feels instant
      input.value         = newQty;
      minusBtn.disabled   = newQty <= 1;
      minusBtn.classList.toggle('is-disabled', newQty <= 1);

      await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body:    JSON.stringify({ id: itemKey, quantity: newQty }),
      });
      // Global fetch patch picks up /cart/change and calls
      // syncCartDOM() + render() — nothing else needed here.
    }

    minusBtn.addEventListener('click', () => changeQty(-1));
    plusBtn.addEventListener('click',  () => changeQty(+1));
  }

  // ── Core cart DOM sync — pure /cart.js, no section rendering ─
  //
  // Strategy:
  //  1. Fetch /cart.js for the ground truth.
  //  2. Walk existing cart item rows and update qty / price in-place.
  //  3. Inject any new items (added from upsell) as built HTML.
  //  4. Remove rows for items that are no longer in the cart.
  //  5. Update heading count, subtotal, and shipping bar values.
  //  6. Toggle is-empty on the drawer root.
  //
  // No element is ever replaced — only .textContent / .value /
  // classList / dataset properties are written, so all theme
  // event listeners remain alive.

  async function syncCartDOM() {
    const cart   = await getCart();
    const drawer = document.getElementById('CartDrawer');
    const body   = drawer && drawer.querySelector('.cart-items__body');

    // ── 1. Update / insert line items ───────────────────────
    if (body) {
      const liveKeys = new Set(cart.items.map(i => i.key));

      // Remove rows for deleted items
      body.querySelectorAll('[data-cart-item]').forEach(row => {
        if (!liveKeys.has(row.dataset.cartItem)) row.remove();
      });

      // Update existing rows and append missing ones
      cart.items.forEach((item, idx) => {
        const index   = idx + 1;
        const existing = body.querySelector(`[data-cart-item="${item.key}"]`);

        if (existing) {
          // Update quantity input
          const qtyInput = existing.querySelector('.quantity__input');
          if (qtyInput) qtyInput.value = item.quantity;

          // Update minus button disabled state
          const minusBtn = existing.querySelector('[data-quantity-decrease]');
          if (minusBtn) {
            minusBtn.disabled = item.quantity <= 1;
            minusBtn.classList.toggle('is-disabled', item.quantity <= 1);
          }

          // Update line price
          const priceEl = existing.querySelector('.cart-item__price');
          if (priceEl) priceEl.textContent = moneyFormat(item.final_line_price);
        } else {
          // New item — build and insert at the correct position
          const html    = buildCartItemHTML(item, index);
          const wrapper = document.createElement('div');
          wrapper.innerHTML = html;
          const newRow  = wrapper.firstElementChild;

          // Insert before the row that will become index+1, or append
          const rows    = body.querySelectorAll('[data-cart-item]');
          if (rows[idx]) {
            body.insertBefore(newRow, rows[idx]);
          } else {
            body.appendChild(newRow);
          }

          // The theme's qty JS only runs on page load, so newly
          // injected rows never get its handlers. We wire our own
          // here — they call /cart/change.js which our global fetch
          // patch picks up to re-sync the DOM and upsell row.
          attachQtyHandlers(newRow, item.key);
        }
      });
    }

    // ── 2. Update cart heading count ────────────────────────
    if (drawer) {
      const title = drawer.querySelector('.drawer__title');
      if (title) {
        // Preserve whatever heading text the merchant set, just
        // update the count in parentheses (or add it if missing).
        const base = title.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
        title.textContent = cart.item_count > 0
          ? `${base} (${cart.item_count})`
          : base;
      }
    }

    // ── 3. Update subtotal ──────────────────────────────────
    const subtotalVal = document.querySelector('.cart-footer__subtotal-value');
    if (subtotalVal) subtotalVal.textContent = moneyFormat(cart.total_price);

    // Also update the subtotal label inside the footer block
    const subtotalSpan = document.querySelector('.js-cart-subtotal .cart-footer__subtotal-value');
    if (subtotalSpan) subtotalSpan.textContent = moneyFormat(cart.total_price);

    // ── 4. Update shipping bar ──────────────────────────────
    const shippingEl = document.querySelector('.js-cart-shipping');
    if (shippingEl) {
      const threshold = parseFloat(shippingEl.dataset.amountCents) || 0;
      const progress  = threshold > 0
        ? Math.min((cart.total_price / threshold) * 100, 100)
        : 100;
      shippingEl.dataset.cartTotal = cart.total_price;
      shippingEl.style.setProperty('--shipping-bar-progress-value', `${progress}%`);

      const progressBar  = shippingEl.querySelector('.js-cart-shipping-progress-bar');
      const label        = shippingEl.querySelector('.js-cart-shipping-label');
      const amountSpan   = shippingEl.querySelector('.js-cart-shipping-amount-msg');
      const successMsg   = shippingEl.dataset.successMessage || 'You have free shipping';
      const pendingMsg   = shippingEl.dataset.amountMessage  || 'You are only {amount} away from free shipping';

      if (progress >= 100) {
        if (label)      label.classList.add('cart-shipping__label--success');
        if (progressBar) progressBar.classList.add('is-hidden');
        if (amountSpan) amountSpan.textContent = successMsg;
      } else {
        const remaining = threshold - cart.total_price;
        if (label)      label.classList.remove('cart-shipping__label--success');
        if (progressBar) progressBar.classList.remove('is-hidden');
        if (amountSpan) amountSpan.textContent = pendingMsg.replace('{amount}', moneyFormat(remaining));
      }
    }

    // ── 5. Header cart count badge (mini-cart icon) ─────────
    document.querySelectorAll('[data-cart-count], .js-cart-count, .cart-count').forEach(el => {
      el.textContent = cart.item_count;
    });

    // ── 6. Empty state ──────────────────────────────────────
    if (drawer) {
      drawer.classList.toggle('is-empty', cart.item_count === 0);
    }

    return cart;
  }

  // ── Upsell skeleton ────────────────────────────────────────

  function showSkeleton() {
    const t = track();
    if (!t) return;
    t.innerHTML = `
      <div class="cart-upsell__skeleton" aria-hidden="true">
        <div class="cart-upsell__skeleton-img"></div>
        <div class="cart-upsell__skeleton-line"></div>
        <div class="cart-upsell__skeleton-line cart-upsell__skeleton-line--short"></div>
      </div>`;
  }

  // ── Upsell card builder ────────────────────────────────────

  function buildCard(product, cartVariantIds) {
    const tpl = cardTemplate();
    if (!tpl) return null;

    const node      = tpl.content.cloneNode(true);
    const card      = node.querySelector('.cart-upsell__card');
    const imgLink   = node.querySelector('.cart-upsell__img-link');
    const img       = node.querySelector('.cart-upsell__img');
    const titleLink = node.querySelector('.cart-upsell__title');
    const price     = node.querySelector('.cart-upsell__price');
    const addBtn    = node.querySelector('.cart-upsell__add');

    const variant = product.variants && product.variants[0];
    if (!variant) return null;

    card.dataset.productId = product.id;
    card.dataset.variantId = variant.id;
    imgLink.href           = product.url;
    titleLink.href         = product.url;
    img.src                = product.featured_image
      ? product.featured_image.replace(/\.(jpg|jpeg|png|webp)/i, '_120x120.$1')
      : '';
    img.alt                = product.title;
    titleLink.textContent  = product.title;
    price.textContent      = moneyFormat(variant.price);

    if (cartVariantIds.has(variant.id)) {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-label', 'Already in cart');
      addBtn.querySelector('.cart-upsell__add-icon').textContent = '\u2713';
    } else {
      addBtn.addEventListener('click', () => handleAddClick(addBtn, card, variant.id, product.id));
    }

    return card;
  }

  // ── Add to cart from upsell card ──────────────────────────

  async function handleAddClick(btn, card, variantId, productId) {
    const icon    = btn.querySelector('.cart-upsell__add-icon');
    const spinner = btn.querySelector('.cart-upsell__add-spinner');
    btn.disabled  = true;
    icon.hidden   = true;
    spinner.hidden = false;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
        __fromUpsell: true,
      });
      if (!res.ok) throw new Error('Add to cart failed');

      setLastProductId(productId);

      // Sync cart DOM from /cart.js then refresh upsell row
      await syncCartDOM();
      await render(productId);
    } catch (err) {
      console.error(err);
      icon.hidden    = false;
      spinner.hidden = true;
      btn.disabled   = false;
    }
  }

  // ── Upsell row renderer ────────────────────────────────────

  async function render(productId) {
    const r = root();
    const t = track();
    if (!r || !t || !productId) return;

    showSkeleton();

    try {
      const [cart, products] = await Promise.all([
        getCart(),
        fetchRecommendations(productId),
      ]);

      const cartVariantIds = new Set(cart.items.map(i => i.variant_id));

      let filtered = products.filter(p => {
        const v = p.variants && p.variants[0];
        return v && !cartVariantIds.has(v.id);
      });
      if (filtered.length === 0) filtered = products;
      if (filtered.length === 0) { r.hidden = true; return; }

      const frag = document.createDocumentFragment();
      filtered.slice(0, 4).forEach(p => {
        const c = buildCard(p, cartVariantIds);
        if (c) frag.appendChild(c);
      });

      t.innerHTML = '';
      t.appendChild(frag);
      r.hidden = false;
    } catch (err) {
      console.error('CartUpsell render failed:', err);
      r.hidden = true;
    }
  }

  // ── Public refresh ─────────────────────────────────────────

  async function refresh(productId) {
    if (productId) { setLastProductId(productId); return render(productId); }
    const stored = getLastProductId();
    if (stored) return render(stored);
    try {
      const cart = await getCart();
      const last = cart.items[cart.items.length - 1];
      if (last) { setLastProductId(last.product_id); return render(last.product_id); }
    } catch (e) {}
  }

  window.CartUpsell = { refresh, render, syncCartDOM };

  // ── Global cart-mutation watcher ───────────────────────────
  //
  // Patches window.fetch to catch qty +/- and remove calls
  // (/cart/change, /cart/update) made by the theme's own buttons,
  // and re-syncs cart DOM + upsell row via /cart.js.

  if (!window.__cartUpsellFetchPatched) {
    window.__cartUpsellFetchPatched = true;
    const _fetch           = window.fetch;
    const MUTATION_RE      = /\/cart\/(change|update|clear)(\.js)?(\?|$)/;

    window.fetch = function (...args) {
      const req  = args[0];
      const url  = typeof req === 'string' ? req : req && req.url;
      const isMutation = url && MUTATION_RE.test(url);

      const result = _fetch.apply(this, args);

      if (isMutation) {
        result
          .then(() => syncCartDOM())
          .then(cart => {
            if (!cart || cart.items.length === 0) {
              const r = root();
              if (r) r.hidden = true;
              return;
            }
            const seed = cart.items[cart.items.length - 1];
            setLastProductId(seed.product_id);
            render(seed.product_id);
          })
          .catch(err => console.error('CartUpsell sync failed:', err));
      }

      return result;
    };
  }

  // ── Init ───────────────────────────────────────────────────

  document.addEventListener('cart-drawer:open', () => refresh());
  document.addEventListener('DOMContentLoaded',  () => refresh());
})();